import { InformationModal, DefaultPrimaryButton } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import type { exchangeRateSupplier } from "@/api/exchangeRateService";
import { useEffect, useState } from "react";

interface SaveSupplierModalProps {
  open: boolean;
  handleClose: () => void;
  selectedSupplier?: exchangeRateSupplier;
  handleAddSupplier: (data: exchangeRateSupplier) => void;
  handleEditSupplier: (data: exchangeRateSupplier) => void;
}

const roundingModeOptions = [
  { value: "ROUND UP", label: "Round Up" },
  { value: "ROUND DOWN", label: "Round Down" },
  { value: "ROUND", label: "Round" },
];

const SaveSupplierModal = ({
  open,
  handleClose,
  selectedSupplier,
  handleAddSupplier,
  handleEditSupplier,
}: SaveSupplierModalProps) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<exchangeRateSupplier>({
    name: "",
    name_abbr: "",
    rounding: "ROUND",
    rounding_digits: 4,
    is_active: true,
  });

  useEffect(() => {
    if (selectedSupplier) {
      setFormData({
        id: selectedSupplier.id,
        name: selectedSupplier.name,
        name_abbr: selectedSupplier.name_abbr,
        rounding: selectedSupplier.rounding || "ROUND",
        rounding_digits: selectedSupplier.rounding_digits || 4,
        is_active: selectedSupplier.is_active ?? true,
      });
    }
  }, [selectedSupplier]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = "กรุณากรอกชื่อ Supplier";
    }

    if (!formData.name_abbr) {
      newErrors.name_abbr = "กรุณากรอกตัวย่อ";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validateForm()) return;
    handleAddSupplier(formData);
    onClose();
  };

  const handleEdit = () => {
    if (!validateForm()) return;
    handleEditSupplier(formData);
    onClose();
  };

  const onClose = () => {
    setFormData({
      name: "",
      name_abbr: "",
      rounding: "ROUND",
      rounding_digits: 4,
      is_active: true,
    });
    setErrors({});
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2">
          {selectedSupplier ? "แก้ไข Supplier" : "เพิ่ม Supplier ใหม่"}
        </Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <TextField
            fullWidth
            label={
              <>
                ชื่อ Supplier{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            variant="outlined"
            sx={{ mt: 2 }}
            value={formData.name}
            error={!!errors.name}
            helperText={errors.name}
            onChange={(e) => {
              setFormData({
                ...formData,
                name: e.target.value,
              });
              if (errors.name) {
                setErrors({ ...errors, name: "" });
              }
            }}
          />
          <TextField
            fullWidth
            label={
              <>
                ตัวย่อ{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </>
            }
            variant="outlined"
            sx={{ mt: 2 }}
            value={formData.name_abbr}
            error={!!errors.name_abbr}
            helperText={errors.name_abbr}
            onChange={(e) => {
              setFormData({ ...formData, name_abbr: e.target.value });
              if (errors.name_abbr) {
                setErrors({ ...errors, name_abbr: "" });
              }
            }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Rounding Mode</InputLabel>
            <Select
              value={formData.rounding}
              label="Rounding Mode"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  rounding: e.target.value as
                    | "ROUND"
                    | "ROUND DOWN"
                    | "ROUND UP",
                })
              }
            >
              {roundingModeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="จำนวนทศนิยม (หลัก)"
            type="number"
            variant="outlined"
            sx={{ mt: 2 }}
            value={formData.rounding_digits}
            onChange={(e) => {
              const value = Math.floor(Number(e.target.value));
              setFormData({
                ...formData,
                rounding_digits: value >= 0 ? value : 0,
              });
            }}
            inputProps={{ min: 0, max: 10, step: 1 }}
          />
        </Box>
        {selectedSupplier && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <Switch
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
            />
            <Typography fontWeight="bold">Active</Typography>
          </Box>
        )}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={selectedSupplier ? handleEdit : handleAdd}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveSupplierModal;
